import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useBusiness } from "@/context/BusinessContext";
import { apiAdmin, buildFileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Upload, Trash2, ImageIcon, Film, Loader2, ListPlus, Folder, FolderPlus,
  FolderOpen, Tag, Plus, X, Pencil, Move, Search, Inbox, Download,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import AddToPlaylistsDialog from "@/components/AddToPlaylistsDialog";

const ALL = "_all_";
const UNFILED = "_none_";

export default function ContentLibraryPage() {
  const { activeBusiness } = useBusiness();
  const [media, setMedia] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folderMeta, setFolderMeta] = useState({ unfiled_count: 0, total_count: 0 });
  const [tags, setTags] = useState([]);

  const [activeFolder, setActiveFolder] = useState(ALL);
  const [activeTag, setActiveTag] = useState("");
  const [search, setSearch] = useState("");

  const [uploading, setUploading] = useState(false);
  const [uploads, setUploads] = useState({}); // {filename: {progress, speed, eta, size, sent}}
  const [addToPlMedia, setAddToPlMedia] = useState(null);
  const [tagEditMedia, setTagEditMedia] = useState(null);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [renameFolder, setRenameFolder] = useState(null);
  const fileRef = useRef(null);

  const loadFoldersAndTags = useCallback(async () => {
    if (!activeBusiness) return;
    try {
      const [fRes, tRes, plRes] = await Promise.all([
        apiAdmin.get(`/folders?business_id=${activeBusiness.id}`),
        apiAdmin.get(`/tags?business_id=${activeBusiness.id}`),
        apiAdmin.get(`/playlists?business_id=${activeBusiness.id}`),
      ]);
      setFolders(fRes.data.folders || []);
      setFolderMeta({
        unfiled_count: fRes.data.unfiled_count || 0,
        total_count: fRes.data.total_count || 0,
      });
      setTags(tRes.data || []);
      setPlaylists(plRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [activeBusiness]);

  const loadMedia = useCallback(async () => {
    if (!activeBusiness) return;
    try {
      const params = new URLSearchParams({ business_id: activeBusiness.id });
      if (activeFolder !== ALL) params.set("folder_id", activeFolder);
      if (activeTag) params.set("tag", activeTag);
      const { data } = await apiAdmin.get(`/media?${params.toString()}`);
      setMedia(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [activeBusiness, activeFolder, activeTag]);

  useEffect(() => { loadFoldersAndTags(); }, [loadFoldersAndTags]);
  useEffect(() => { loadMedia(); }, [loadMedia]);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadFoldersAndTags(), loadMedia()]);
  }, [loadFoldersAndTags, loadMedia]);

  const filteredMedia = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return media;
    return media.filter((m) => m.filename?.toLowerCase().includes(q));
  }, [media, search]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !activeBusiness) return;
    // Client-side guard. Mirror MAX_UPLOAD_MB on the backend — default 5 GB (Supabase Pro per-file ceiling).
    // The actual cap is enforced by Supabase Storage bucket settings + the backend env var.
    const MAX_BYTES = 5 * 1024 * 1024 * 1024;
    const tooBig = files.filter((f) => f.size > MAX_BYTES);
    if (tooBig.length) {
      tooBig.forEach((f) =>
        toast.error(
          `${f.name} (${(f.size / 1024 / 1024).toFixed(1)} MB) supera el límite de 5 GB por archivo.`,
          { duration: 8000 }
        )
      );
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploading(true);
    setUploads({});
    const folderForUpload = activeFolder !== ALL && activeFolder !== UNFILED ? activeFolder : "";

    // Helper: format human-friendly sizes / durations
    const fmtSize = (b) => (b >= 1024 * 1024 * 1024 ? `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`
      : b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB`
      : `${(b / 1024).toFixed(0)} KB`);
    const fmtEta = (sec) => {
      if (!Number.isFinite(sec) || sec < 1) return "—";
      if (sec < 60) return `${Math.ceil(sec)}s`;
      const m = Math.floor(sec / 60);
      const s = Math.ceil(sec % 60);
      return `${m}m ${s.toString().padStart(2, "0")}s`;
    };

    // Upload a single file DIRECTLY to Supabase via signed URL with real-time
    // progress tracking. We use XMLHttpRequest because `fetch` does not expose
    // upload progress events reliably across browsers.
    const uploadOne = async (f) => {
      const key = f.name;
      setUploads((prev) => ({ ...prev, [key]: { progress: 0, speed: 0, eta: 0, size: f.size, sent: 0 } }));
      try {
        // 1) signed URL
        const { data: init } = await apiAdmin.post("/media/upload-url", {
          business_id: activeBusiness.id,
          filename: f.name,
          content_type: f.type || "application/octet-stream",
        });
        // 2) PUT directly to Supabase with progress
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          const startedAt = Date.now();
          xhr.open("PUT", init.signed_url, true);
          xhr.setRequestHeader("Content-Type", f.type || "application/octet-stream");
          xhr.setRequestHeader("x-upsert", "true");
          xhr.upload.onprogress = (ev) => {
            if (!ev.lengthComputable) return;
            const elapsedSec = Math.max(0.1, (Date.now() - startedAt) / 1000);
            const speed = ev.loaded / elapsedSec; // bytes/sec
            const remaining = Math.max(0, ev.total - ev.loaded);
            const eta = speed > 0 ? remaining / speed : 0;
            setUploads((prev) => ({
              ...prev,
              [key]: {
                progress: Math.round((ev.loaded / ev.total) * 100),
                speed,
                eta,
                size: ev.total,
                sent: ev.loaded,
              },
            }));
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve(null);
            else reject(new Error(`Supabase upload failed (${xhr.status}): ${xhr.responseText?.slice(0, 200) || ""}`));
          };
          xhr.onerror = () => reject(new Error("Network error during upload"));
          xhr.onabort = () => reject(new Error("Upload aborted"));
          xhr.send(f);
        });
        // 3) Finalize
        await apiAdmin.post("/media/upload-finalize", {
          media_id: init.media_id,
          object_path: init.object_path,
          business_id: activeBusiness.id,
          filename: f.name,
          content_type: f.type || "application/octet-stream",
          size: f.size,
          folder_id: folderForUpload || null,
        });
        setUploads((prev) => ({
          ...prev,
          [key]: { ...prev[key], progress: 100, eta: 0, speed: 0, done: true },
        }));
        return true;
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || String(err);
        setUploads((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), error: msg } }));
        toast.error(`${f.name}: ${msg}`, { duration: 10000 });
        return false;
      }
    };

    // expose formatters so the panel renderer can use them
    window.__ccUploadFmt = { fmtSize, fmtEta };

    // Run uploads with limited concurrency (3 at a time)
    const CONCURRENCY = 3;
    let okCount = 0;
    let cursor = 0;
    const worker = async () => {
      while (cursor < files.length) {
        const i = cursor++;
        const ok = await uploadOne(files[i]);
        if (ok) okCount += 1;
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, files.length) }, worker));

    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    // Clear the progress panel after a short delay so the user sees the 100% state
    window.setTimeout(() => setUploads({}), 2000);
    if (okCount === files.length) {
      toast.success(`${okCount} ${okCount === 1 ? "archivo subido" : "archivos subidos"}`);
    } else if (okCount > 0) {
      toast.message(`${okCount} de ${files.length} archivos subidos`);
    }
    refreshAll();
  };

  const removeMedia = async (id) => {
    try {
      await apiAdmin.delete(`/media/${id}`);
      toast.success("Media removed");
      refreshAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const moveTo = async (mediaId, folderId) => {
    try {
      await apiAdmin.patch(`/media/${mediaId}`, { folder_id: folderId });
      toast.success(folderId ? "Moved to folder" : "Moved out of folder");
      refreshAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const createFolder = async (name) => {
    if (!name?.trim()) return;
    try {
      await apiAdmin.post("/folders", { name: name.trim(), business_id: activeBusiness.id });
      toast.success("Folder created");
      refreshAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteFolder = async (id) => {
    try {
      await apiAdmin.delete(`/folders/${id}`);
      toast.success("Folder deleted (media moved to Unfiled)");
      if (activeFolder === id) setActiveFolder(ALL);
      refreshAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const renameFolderSubmit = async (id, name) => {
    if (!name?.trim()) return;
    try {
      await apiAdmin.patch(`/folders/${id}`, { name: name.trim() });
      toast.success("Folder renamed");
      refreshAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadFolder = async (folderId, prettyName) => {
    const toastId = toast.loading(`Packing "${prettyName}" into a ZIP…`);
    try {
      const { data } = await apiAdmin.get(`/folders/${folderId}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${prettyName.replace(/[\\/]/g, "_") || "folder"}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded "${prettyName}.zip"`, { id: toastId });
    } catch (e) {
      const detail = e?.response?.data instanceof Blob
        ? "Folder is empty or unreachable"
        : formatApiError(e);
      toast.error(detail, { id: toastId });
    }
  };

  return (
    <div className="space-y-6" data-testid="content-library-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Library</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
            Content Library
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Organize images and videos for {activeBusiness?.name} into folders and tags.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" strokeWidth={1.25} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search files"
              className="pl-9 h-10 w-56"
              data-testid="content-search-input"
            />
          </div>
          <input
            type="file"
            ref={fileRef}
            multiple
            accept="image/*,video/*,.mp4,.m4v,.mov,.webm,.mkv,.avi,.3gp,.ogv"
            onChange={onUpload}
            className="hidden"
            data-testid="content-upload-input"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="content-upload-button"
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {uploading ? "Uploading..." : "Upload media"}
          </Button>
        </div>
      </div>

      {/* Live upload progress panel — shows each file's % / speed / ETA */}
      {Object.keys(uploads).length > 0 && (
        <div
          className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
          data-testid="upload-progress-panel"
        >
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
            <div className="text-xs uppercase tracking-widest text-slate-600 font-semibold">
              Uploading {Object.keys(uploads).length} {Object.keys(uploads).length === 1 ? "file" : "files"}
            </div>
            <div className="text-xs text-slate-500">
              {Object.values(uploads).filter((u) => u.done).length} / {Object.keys(uploads).length} completed
            </div>
          </div>
          <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
            {Object.entries(uploads).map(([name, u]) => {
              const fmtSize = (b) =>
                !Number.isFinite(b) ? "—"
                : b >= 1024 * 1024 * 1024 ? `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`
                : b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB`
                : `${(b / 1024).toFixed(0)} KB`;
              const fmtEta = (s) =>
                !Number.isFinite(s) || s < 1 ? "—"
                : s < 60 ? `${Math.ceil(s)}s`
                : `${Math.floor(s / 60)}m ${Math.ceil(s % 60).toString().padStart(2, "0")}s`;
              const pct = u.progress || 0;
              const isErr = !!u.error;
              const isDone = !!u.done;
              return (
                <div key={name} className="px-4 py-3" data-testid={`upload-row-${name}`}>
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {isErr ? (
                        <X className="h-3.5 w-3.5 text-red-500 shrink-0" strokeWidth={2.5} />
                      ) : isDone ? (
                        <div className="h-3.5 w-3.5 rounded-full bg-emerald-500 grid place-items-center shrink-0">
                          <span className="text-white text-[8px] font-bold leading-none">✓</span>
                        </div>
                      ) : (
                        <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin shrink-0" />
                      )}
                      <span className="text-sm text-slate-900 truncate font-medium">{name}</span>
                    </div>
                    <span
                      className={`text-xs tabular-nums font-semibold shrink-0 ${
                        isErr ? "text-red-500" : isDone ? "text-emerald-600" : "text-slate-700"
                      }`}
                    >
                      {isErr ? "Error" : `${pct}%`}
                    </span>
                  </div>
                  {!isErr ? (
                    <>
                      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-200 ${
                            isDone ? "bg-emerald-500" : "bg-blue-500"
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-slate-500 mt-1 tabular-nums">
                        <span>
                          {fmtSize(u.sent || 0)} / {fmtSize(u.size || 0)}
                          {u.speed > 0 && !isDone ? ` · ${fmtSize(u.speed)}/s` : ""}
                        </span>
                        <span>{isDone ? "Done" : `ETA ${fmtEta(u.eta)}`}</span>
                      </div>
                    </>
                  ) : (
                    <div className="text-[11px] text-red-500 truncate">{u.error}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar: Folders + Tags */}
        <aside className="col-span-12 lg:col-span-3 space-y-6" data-testid="content-sidebar">
          <section className="rounded-2xl border border-slate-200 bg-white">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <div className="text-xs uppercase tracking-widest text-slate-500">Folders</div>
              <Button
                size="sm" variant="ghost"
                onClick={() => setNewFolderOpen(true)}
                data-testid="folder-add-button"
                title="New folder"
              >
                <FolderPlus className="h-4 w-4" strokeWidth={1.25} />
              </Button>
            </div>
            <div className="p-2 space-y-0.5">
              <FolderRow
                label="All files" count={folderMeta.total_count}
                icon={<Inbox className="h-4 w-4" strokeWidth={1.25} />}
                active={activeFolder === ALL}
                onClick={() => setActiveFolder(ALL)}
                testid="folder-row-all"
                actions={folderMeta.total_count > 0 && (
                  <FolderAction
                    onClick={() => downloadFolder("all", "all-files")}
                    icon={<Download className="h-3.5 w-3.5" strokeWidth={1.5} />}
                    title="Download all files as ZIP"
                    testid="folder-row-all-download"
                  />
                )}
              />
              <FolderRow
                label="Unfiled" count={folderMeta.unfiled_count}
                icon={<Folder className="h-4 w-4" strokeWidth={1.25} />}
                active={activeFolder === UNFILED}
                onClick={() => setActiveFolder(UNFILED)}
                testid="folder-row-unfiled"
                actions={folderMeta.unfiled_count > 0 && (
                  <FolderAction
                    onClick={() => downloadFolder("unfiled", "unfiled")}
                    icon={<Download className="h-3.5 w-3.5" strokeWidth={1.5} />}
                    title="Download unfiled as ZIP"
                    testid="folder-row-unfiled-download"
                  />
                )}
              />
              {folders.length > 0 && <div className="h-px bg-slate-100 my-2" />}
              {folders.map((f) => (
                <FolderRow
                  key={f.id} label={f.name} count={f.media_count || 0}
                  icon={activeFolder === f.id
                    ? <FolderOpen className="h-4 w-4 text-blue-600" strokeWidth={1.25} />
                    : <Folder className="h-4 w-4" strokeWidth={1.25} />}
                  active={activeFolder === f.id}
                  onClick={() => setActiveFolder(f.id)}
                  testid={`folder-row-${f.id}`}
                  actions={
                    <div className="flex items-center gap-0.5">
                      {(f.media_count || 0) > 0 && (
                        <FolderAction
                          onClick={() => downloadFolder(f.id, f.name)}
                          icon={<Download className="h-3.5 w-3.5" strokeWidth={1.5} />}
                          title="Download folder as ZIP"
                          testid={`folder-${f.id}-download`}
                          color="blue"
                        />
                      )}
                      <FolderAction
                        onClick={() => setRenameFolder(f)}
                        icon={<Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />}
                        title="Rename folder"
                        testid={`folder-${f.id}-rename`}
                        color="amber"
                      />
                      <FolderAction
                        onClick={() => deleteFolder(f.id)}
                        icon={<Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />}
                        title="Delete folder"
                        testid={`folder-${f.id}-delete`}
                        color="rose"
                      />
                    </div>
                  }
                />
              ))}
              {folders.length === 0 && (
                <div className="text-xs text-slate-400 italic px-3 py-2">No folders yet</div>
              )}
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <div className="text-xs uppercase tracking-widest text-slate-500">Tags</div>
              {activeTag && (
                <button
                  className="text-xs text-blue-600 hover:text-blue-700"
                  onClick={() => setActiveTag("")}
                  data-testid="tag-clear"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="p-3 flex flex-wrap gap-1.5">
              {tags.length === 0 && (
                <div className="text-xs text-slate-400 italic">No tags yet — add tags from any media.</div>
              )}
              {tags.map((t) => {
                const active = activeTag === t.tag;
                return (
                  <button
                    key={t.tag}
                    onClick={() => setActiveTag(active ? "" : t.tag)}
                    className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                      active
                        ? "border-blue-600 bg-blue-600 text-white"
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                    data-testid={`tag-pill-${t.tag}`}
                  >
                    <Tag className="h-3 w-3" strokeWidth={1.5} />
                    {t.tag}
                    <span className={`text-[10px] ${active ? "text-white/80" : "text-slate-400"}`}>· {t.count}</span>
                  </button>
                );
              })}
            </div>
          </section>
        </aside>

        {/* Grid */}
        <div className="col-span-12 lg:col-span-9">
          <MediaGrid
            media={filteredMedia}
            folders={folders}
            onRemove={removeMedia}
            onAddToPlaylists={setAddToPlMedia}
            onMoveTo={moveTo}
            onEditTags={setTagEditMedia}
          />
        </div>
      </div>

      <AddToPlaylistsDialog
        open={!!addToPlMedia}
        onClose={() => setAddToPlMedia(null)}
        media={addToPlMedia}
        playlists={playlists}
        onSaved={refreshAll}
      />

      <TagEditorDialog
        open={!!tagEditMedia}
        onClose={() => setTagEditMedia(null)}
        media={tagEditMedia}
        onSaved={refreshAll}
      />

      <FolderNameDialog
        open={newFolderOpen}
        title="New folder"
        onClose={() => setNewFolderOpen(false)}
        onSubmit={(name) => { createFolder(name); setNewFolderOpen(false); }}
      />
      <FolderNameDialog
        open={!!renameFolder}
        title="Rename folder"
        initial={renameFolder?.name}
        onClose={() => setRenameFolder(null)}
        onSubmit={(name) => { renameFolderSubmit(renameFolder.id, name); setRenameFolder(null); }}
      />
    </div>
  );
}

function FolderRow({ label, count, icon, active, onClick, actions, testid }) {
  return (
    <div
      className={`group flex items-center justify-between rounded-lg px-3 py-2 text-sm cursor-pointer transition-colors ${
        active ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-slate-50"
      }`}
      onClick={onClick}
      data-testid={testid}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <span
          className={`text-xs ${active ? "text-blue-600" : "text-slate-400"} group-hover:opacity-0 transition-opacity`}
        >
          {count}
        </span>
        <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity -ml-6 group-hover:ml-0">
          {actions}
        </div>
      </div>
    </div>
  );
}

function FolderAction({ onClick, icon, title, testid, color = "slate" }) {
  // Pastel hover swatches matching the rest of the app (TVs page action buttons).
  const tones = {
    slate: "hover:bg-slate-100 hover:text-slate-700",
    blue:  "hover:bg-blue-50 hover:text-blue-600",
    amber: "hover:bg-amber-50 hover:text-amber-600",
    rose:  "hover:bg-rose-50 hover:text-rose-600",
  };
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`h-7 w-7 grid place-items-center rounded-md text-slate-400 transition-colors ${tones[color] || tones.slate}`}
      title={title}
      data-testid={testid}
    >
      {icon}
    </button>
  );
}

function MediaGrid({ media, folders, onRemove, onAddToPlaylists, onMoveTo, onEditTags }) {
  if (media.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
        <div className="mx-auto h-12 w-12 rounded-2xl bg-blue-50 text-blue-600 grid place-items-center">
          <ImageIcon className="h-6 w-6" strokeWidth={1.25} />
        </div>
        <div className="mt-4 font-heading text-xl font-semibold text-slate-900">Nothing here yet</div>
        <p className="text-sm text-slate-500 mt-1">Upload your first asset or pick another folder/tag.</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
      {media.map((m) => (
        <MediaCard
          key={m.id}
          m={m}
          folders={folders}
          onRemove={onRemove}
          onAddToPlaylists={onAddToPlaylists}
          onMoveTo={onMoveTo}
          onEditTags={onEditTags}
        />
      ))}
    </div>
  );
}

function MediaCard({ m, folders, onRemove, onAddToPlaylists, onMoveTo, onEditTags }) {
  return (
    <div
      className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-md transition-shadow"
      data-testid={`media-card-${m.id}`}
    >
      <div className="aspect-square bg-slate-100 relative">
        {m.media_type === "image" ? (
          <img src={buildFileUrl(m.id, "admin")} alt={m.filename} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <>
            <video
              src={`${buildFileUrl(m.id, "admin")}#t=0.1`}
              className="w-full h-full object-cover bg-black"
              muted
              playsInline
              preload="metadata"
            />
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="h-12 w-12 rounded-full bg-black/55 backdrop-blur-sm border border-white/25 grid place-items-center text-white">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              </div>
            </div>
          </>
        )}
        <div className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-white/90 backdrop-blur px-2 py-0.5 text-[10px] uppercase tracking-widest text-slate-700 border border-slate-200">
          {m.media_type === "image" ? <ImageIcon className="h-3 w-3" /> : <Film className="h-3 w-3" />}
          {m.media_type}
        </div>
      </div>
      <div className="p-3">
        <div className="text-sm font-medium text-slate-900 truncate">{m.filename}</div>
        <div className="text-[11px] text-slate-500">{(m.size / 1024).toFixed(0)} KB</div>

        {/* Tags row */}
        <div className="mt-2 flex flex-wrap gap-1 min-h-[22px]">
          {(m.tags || []).slice(0, 4).map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] text-slate-600"
            >
              <Tag className="h-2.5 w-2.5" strokeWidth={1.5} />
              {t}
            </span>
          ))}
          {(m.tags || []).length > 4 && (
            <span className="text-[10px] text-slate-400">+{m.tags.length - 4}</span>
          )}
          <button
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 text-slate-500 hover:border-blue-500 hover:text-blue-600 px-2 py-0.5 text-[10px]"
            onClick={() => onEditTags(m)}
            data-testid={`media-${m.id}-edit-tags`}
          >
            <Plus className="h-2.5 w-2.5" strokeWidth={1.5} /> tag
          </button>
        </div>

        {/* Actions */}
        <div className="mt-3 flex items-center justify-between">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-slate-600" data-testid={`media-${m.id}-move`}>
                <Move className="h-4 w-4 mr-1" strokeWidth={1.25} /> Move
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 max-h-72 overflow-y-auto">
              <DropdownMenuLabel className="text-xs text-slate-500">Move to folder</DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={() => onMoveTo(m.id, null)}
                data-testid={`media-${m.id}-move-unfiled`}
              >
                <Folder className="h-4 w-4 mr-2" strokeWidth={1.25} /> Unfiled
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {folders.length === 0 && (
                <DropdownMenuItem disabled>
                  <span className="text-xs text-slate-400">No folders yet</span>
                </DropdownMenuItem>
              )}
              {folders.map((f) => (
                <DropdownMenuItem
                  key={f.id}
                  onSelect={() => onMoveTo(m.id, f.id)}
                  data-testid={`media-${m.id}-move-${f.id}`}
                >
                  <Folder className="h-4 w-4 mr-2" strokeWidth={1.25} /> {f.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost" size="sm"
              onClick={() => onAddToPlaylists(m)}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              data-testid={`media-${m.id}-add-to-playlists`}
              title="Add to playlists"
            >
              <ListPlus className="h-4 w-4" strokeWidth={1.25} />
            </Button>
            <Button
              variant="ghost" size="sm"
              onClick={() => onRemove(m.id)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid={`media-${m.id}-delete`}
              title="Delete"
            >
              <Trash2 className="h-4 w-4" strokeWidth={1.25} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FolderNameDialog({ open, title, initial = "", onClose, onSubmit }) {
  const [name, setName] = useState("");
  useEffect(() => { if (open) setName(initial || ""); }, [open, initial]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="folder-name-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Folders help group related media within this business.</DialogDescription>
        </DialogHeader>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Happy Hour · Brunch · Promos"
          className="mt-2"
          autoFocus
          data-testid="folder-name-input"
          onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) onSubmit(name); }}
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="folder-name-cancel">Cancel</Button>
          <Button onClick={() => onSubmit(name)} disabled={!name.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="folder-name-submit">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TagEditorDialog({ open, onClose, media, onSaved }) {
  const [tags, setTags] = useState([]);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTags([...(media?.tags || [])]);
      setDraft("");
    }
  }, [open, media]);

  const add = (raw) => {
    const value = (raw ?? draft).trim();
    if (!value) return;
    if (tags.includes(value)) {
      setDraft("");
      return;
    }
    setTags((prev) => [...prev, value]);
    setDraft("");
  };
  const remove = (t) => setTags((prev) => prev.filter((x) => x !== t));

  const submit = async () => {
    if (!media) return;
    setSaving(true);
    try {
      await apiAdmin.patch(`/media/${media.id}`, { tags });
      toast.success("Tags updated");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="tag-editor-dialog">
        <DialogHeader>
          <DialogTitle>Tags for {media?.filename}</DialogTitle>
          <DialogDescription>
            Press Enter or comma to add a tag. Tags help you filter and find content fast.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3">
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 flex flex-wrap items-center gap-1.5">
            {tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 text-xs"
              >
                <Tag className="h-3 w-3" strokeWidth={1.5} />
                {t}
                <button onClick={() => remove(t)} className="ml-0.5 text-blue-600 hover:text-blue-800" data-testid={`tag-editor-remove-${t}`}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  add();
                } else if (e.key === "Backspace" && !draft && tags.length) {
                  remove(tags[tags.length - 1]);
                }
              }}
              placeholder={tags.length ? "Add another…" : "Type tag and press Enter"}
              className="flex-1 min-w-[120px] text-sm outline-none bg-transparent py-1"
              data-testid="tag-editor-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="tag-editor-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="tag-editor-save">
            {saving ? "Saving…" : "Save tags"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
